import PropTypes from 'prop-types';

// third-party
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';

import usePageAccess from 'hooks/usePageAccess';

/**
 * A one-line notice that the signed-in user holds this screen at Full-Read — they may
 * read and export but not change anything. Renders nothing when the user has Full access
 * (or while the menu is still loading), so it is safe to drop at the top of any page:
 *
 *   <ReadOnlyBanner code="child_profiles" />
 *
 * Pass the SAME catalogue code the page already uses with `canSeeCode`. The write controls
 * on the page should be disabled off the same `usePageAccess().readOnly(code)` — this banner
 * only explains why. The backend is what actually refuses the write; this keeps the UI honest.
 */
export default function ReadOnlyBanner({ code, sx }) {
  const intl = useIntl();
  const { readOnly } = usePageAccess();

  if (!readOnly(code)) return null;

  return (
    <Alert severity="info" variant="outlined" sx={{ mb: 2, ...sx }}>
      {intl.formatMessage({
        id: 'accessLevel.readOnlyBanner',
        defaultMessage: 'You have read-only access to this page. You can view and export, but changes are disabled.'
      })}
    </Alert>
  );
}

ReadOnlyBanner.propTypes = {
  code: PropTypes.string.isRequired,
  sx: PropTypes.object
};
